import isMedia from '@zr/utilities/isMedia';

import {
  GoogleCredentialResponse,
  GoogleSignInCallbackInterface,
  LoggingOptions,
  LoginOrRegData,
} from './google-sign-in.d';

/**
 *  Loads the google library and renders the google sign in button into the given element.
 *  This function will load in the google api dynamically, so it's not required to include the script tag in your html.
 *  @param {string} googleClientId - Client ID needed to authenticate with Google's API. Typically stashed in the global window object as GOOGLE_SIGNIN_CLIENT_ID
 *  https://developers.google.com/identity/gsi/web/reference/js-reference#client_id
 *  @param {callback} handleCredentialResponse - Callback function for the behavior after a user clicks the google sign in button and signs in.
 *  You could put in loadingAnimations and a call to `loginOrRegisterWithGoogle` in order to register the user with our API.
 *  @param {Element} googleButtonElement - The DOM element into which the google button should render. An empty div will suffice.
 */

export function renderGoogleButton(
  googleClientId: string, handleCredentialResponse: GoogleSignInCallbackInterface, googleButtonElement: HTMLElement,
): void {
  const isMobile = isMedia('phonesAndTabletSmall');

  window.google.accounts.id.initialize({
    client_id: googleClientId,
    callback: handleCredentialResponse,
  });

  // The width of the google button needs to be slightly narrower for mobile devices.
  // Note that since we determine this via JS, a full page refresh is needed to see the smaller button render when using devtools.
  // If you need more customization over the width of the button, consider adding a function to query for more device sizes beyond
  // `phonesAndTabletSmall`. A function could be written to create a mapping of device sizes to various widths of the google button. e.g.
  // width = { phoneVerySmall: 300, phoneMedium: 320, phoneLarge: 350, desktop: 400 }[mediaSize]

  // render the button onto the page
  window.google.accounts.id.renderButton(
    googleButtonElement,
    {
      logo_alignment: 'center',
      shape: 'pill',
      size: 'large',
      text: 'continue_with',
      theme: 'outline',
      type: 'standard',
      width: isMobile ? '300' : '400',
    },
  );
}


export function loadAndRenderGoogleButton(
  googleClientId: string, handleCredentialResponse: GoogleSignInCallbackInterface, googleButtonElement: HTMLElement,
): void {
  const googleScriptId = 'google-gsi-script';
  let googleScriptElement = document.getElementById(googleScriptId);

  // if these global variables provided by google are present, the google library has already been downloaded and is ready to use.
  if (window.google && window.google.accounts) {
    renderGoogleButton(googleClientId, handleCredentialResponse, googleButtonElement);
    return;
  }

  if (!googleScriptElement) {
    const newGoogleScriptElement = document.createElement('script');
    newGoogleScriptElement.setAttribute('id', googleScriptId);
    newGoogleScriptElement.src = 'https://accounts.google.com/gsi/client';
    // Dynamically add the script to the DOM. This allows us to load in the google client library in-line with this JS file, without needing to insert
    // a <script> tag elsewhere in an html file.
    document.head.appendChild(newGoogleScriptElement);
    googleScriptElement = newGoogleScriptElement;
  }

  googleScriptElement.addEventListener('load', () => {
    renderGoogleButton(googleClientId, handleCredentialResponse, googleButtonElement);
  });
}

/**
 *  This function is meant to be used within your implementation of handleCredentialResponse. This will take the auth data from google
 *  and POST it to our login or register endpoint.
 *
 * @param {Object} googleResponseObject - This is the GoogleCredentialResponse object returned by the google api. https://developers.google.com/identity/gsi/web/reference/js-reference#CredentialResponse
 * @param {Object} loggingOptions - reg_method and login_method to be logged after successful sign in
 *
 * @example
 * function handleGoogleSignIn(googleResponseObject) {
 *   beginLoadingAnimation();
 *   loginOrRegisterWithGoogle(
 *     googleResponseObject,
 *     { regMethod: 'google_reg_button.sign_in_page', loginMethod: 'google_login_button.sign_in_page' }
 *   ).then(jsonResponse => {
 *       finishLoadingAnimation();
 *   });
 * }
 * loadAndRenderGoogleButton(zr.page.model.GOOGLE_SIGNIN_CLIENT_ID, handleGoogleSignIn, document.getElementById('google_sign_in'));
 * @returns {Promise} Returns a Promise containing the JSON response from our internal /google_login_or_reg API .
 */

export async function loginOrRegisterWithGoogle(
  googleResponseObject: GoogleCredentialResponse,
  loggingOptions: LoggingOptions,
): Promise<LoginOrRegData> {
  const formData = new FormData();
  formData.append('token', googleResponseObject.credential);
  formData.append('reg_method', loggingOptions.regMethod);
  formData.append('login_method', loggingOptions.loginMethod);

  const response = await fetch('/auth/api/google_login_or_reg/v1', {
    headers: {
      'x-requested-with': 'XMLHttpRequest',
    },
    method: 'POST',
    body: formData,
  });

  return (await response.json()) as LoginOrRegData;
}
